import { Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useFormatNumber from 'hooks/useFormatNumber';
import AirbnbIcon from 'assets/images/icons/channels/airbnb.svg?react';
import { createColumnHelper, Row } from '@tanstack/react-table';
import CommonTable from 'components/table/CommonTable';
import useServerFilteredTable from 'components/table/hooks/useServerFilteredTable';
import { useHostQualityContext } from '../useHostQualityContext';
import { HostingQualityTitle } from '../HostingQualityWrapper.styles';
import { propertyReviewsQuery } from './ListingQuality.utils';
import { AirbnbAnalyticsPropertyReview } from './types/HostQuality.types';

const ValueCell = ({ getValue }: { getValue: () => number | null }) => {
  const formatNumber = useFormatNumber();

  if (getValue() === null) {
    return '-';
  }

  return formatNumber({
    value: getValue()!,
  });
};

const PercentageCell = ({ getValue }: { getValue: () => number | null }) => {
  const formatNumber = useFormatNumber();

  if (getValue() === null) {
    return '-';
  }

  return `${formatNumber({
    value: getValue()! * 100,
  })}%`;
};

const ActionsCell = <T extends AirbnbAnalyticsPropertyReview>({
  row,
}: {
  row: Row<T>;
}) => {
  const { t } = useTranslation();
  const propertyUrl = `#/property/${row.original.propertyUid}/reviews`;

  return (
    <a
      href={propertyUrl}
      target="_blank"
      rel="noreferrer"
      className="btn btn-link"
    >
      {t('common.viewMore')}
    </a>
  );
};

const ListingQuality = () => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<AirbnbAnalyticsPropertyReview>();
  const { hostQualityFilter } = useHostQualityContext();

  const columns = [
    columnHelper.accessor('propertyName', {
      header: t('pageAnalytics.listingQualityColumn.propertyName'),
      enableSorting: true,
    }),
    columnHelper.accessor('qualityStatus', {
      header: t('pageAnalytics.listingQualityColumn.qualityStatus'),
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) {
          return '-';
        }
        return t(`pageAnalytics.qualityStatus.${value}`);
      },
    }),
    columnHelper.accessor('overallScore', {
      header: t('pageAnalytics.listingQualityColumn.overallScore'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('fiveStarReviews', {
      header: t('pageAnalytics.listingQualityColumn.fiveStarReviews'),
      enableSorting: true,
      cell: PercentageCell,
    }),
    columnHelper.accessor('cleanliness', {
      header: t('pageAnalytics.listingQualityColumn.cleanliness'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('accuracy', {
      header: t('pageAnalytics.listingQualityColumn.accuracy'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('checkin', {
      header: t('pageAnalytics.listingQualityColumn.checkin'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('communication', {
      header: t('pageAnalytics.listingQualityColumn.communication'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('location', {
      header: t('pageAnalytics.listingQualityColumn.location'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('value', {
      header: t('pageAnalytics.listingQualityColumn.value'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.accessor('recentTripIssues', {
      header: t('pageAnalytics.listingQualityColumn.recentTripIssues'),
      enableSorting: true,
      cell: ValueCell,
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common.actions'),
      cell: ActionsCell,
    }),
  ];

  const { tableInstance, isLoading } = useServerFilteredTable({
    columns,
    getQuery: (options) =>
      propertyReviewsQuery({
        propertyUid: hostQualityFilter?.propertyUid,
        ownerUid: hostQualityFilter?.ownerUid,
        location: hostQualityFilter?.location,
        ...options,
      }),
    pageSize: 20,
  });

  return (
    <Col xs={12}>
      <HostingQualityTitle>
        {t('pageAnalytics.listingQuality')} <AirbnbIcon />
      </HostingQualityTitle>

      <CommonTable
        tableInstance={tableInstance}
        isLoading={isLoading}
        emptyState={t('pageAnalytics.noListingQualityData')}
        data-testid="listing-quality-table"
      />
    </Col>
  );
};

export default ListingQuality;
